import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { Search, Filter, Download, MoreHorizontal, Eye, CheckCircle2, Truck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGE = {
  Completed: 'bg-emerald-500/10 text-emerald-400',
  Scheduled: 'bg-blue-500/10 text-blue-400',
  'In Progress': 'bg-amber-500/10 text-amber-400',
  Pending: 'bg-gray-500/10 text-gray-400',
};

export default function Reservations() {
  const ctx = useOutletContext();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [depositFilter, setDepositFilter] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    adminApi.getReservations().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = data.filter(r => {
    if (search) {
      const s = search.toLowerCase();
      if (!r.student_name.toLowerCase().includes(s) && !r.email.toLowerCase().includes(s) && !r.reservation_number.toLowerCase().includes(s)) return false;
    }
    if (schoolFilter && r.school !== schoolFilter) return false;
    if (depositFilter === 'paid' && !r.deposit_paid) return false;
    if (depositFilter === 'pending' && r.deposit_paid) return false;
    return true;
  });

  const paged = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);
  const schools = [...new Set(data.map(r => r.school))].sort();

  const handleMarkPaid = async (id) => {
    await adminApi.markDepositPaid(id);
    toast.success('Deposit marked as paid');
    setData(prev => prev.map(r => r._id === id ? { ...r, deposit_paid: true } : r));
  };

  const exportCsv = () => {
    const headers = 'Reservation #,Student,Email,Phone,School,Residence,Deposit,Pickup Status,Delivery Status,Items,Balance\n';
    const rows = filtered.map(r => `${r.reservation_number},${r.student_name},${r.email},${r.phone},${r.school},${r.residence_hall},${r.deposit_paid ? 'Paid' : 'Pending'},${r.pickup_status},${r.delivery_status},${r.item_count},$${r.balance_due}`).join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'reservations.csv'; a.click();
    toast.success('CSV exported');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;

  return (
    <div data-testid="admin-reservations">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Reservations</h1>
          <p className={`text-sm ${ctx.textSecondary} mt-1`}>{filtered.length} total reservations</p>
        </div>
        <button onClick={exportCsv} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="export-csv-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4 mb-4`}>
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${ctx.textSecondary}`} />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name, email, or reservation #..."
              className={`w-full pl-10 pr-4 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none focus:ring-2 focus:ring-blue-500/30`}
              data-testid="reservations-search" />
          </div>
          <select value={schoolFilter} onChange={e => { setSchoolFilter(e.target.value); setPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none`} data-testid="filter-school">
            <option value="">All Schools</option>
            {schools.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={depositFilter} onChange={e => { setDepositFilter(e.target.value); setPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none`} data-testid="filter-deposit">
            <option value="">All Deposits</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reservations-table">
            <thead>
              <tr className={`border-b ${ctx.borderColor}`}>
                {['Reservation #', 'Student', 'School', 'Deposit', 'Pickup', 'Delivery', 'Items', 'Balance', 'Actions'].map(h => (
                  <th key={h} className={`text-left px-4 py-3 text-xs font-medium ${ctx.textSecondary} uppercase tracking-wider`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map(r => (
                <tr key={r._id} data-testid={`reservation-row-${r._id}`} className={`border-b ${ctx.borderColor} ${ctx.hoverBg} transition-colors`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.reservation_number}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-sm">{r.student_name}</p>
                    <p className={`text-xs ${ctx.textSecondary}`}>{r.email}</p>
                  </td>
                  <td className="px-4 py-3 text-xs">{r.school}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${r.deposit_paid ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {r.deposit_paid ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${STATUS_BADGE[r.pickup_status] || STATUS_BADGE.Pending}`}>{r.pickup_status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${STATUS_BADGE[r.delivery_status] || STATUS_BADGE.Pending}`}>{r.delivery_status}</span>
                  </td>
                  <td className="px-4 py-3 text-center">{r.item_count}</td>
                  <td className="px-4 py-3 font-medium">${r.balance_due}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Link to={`/admin/reservations/${r._id}`} className="p-1.5 rounded-md hover:bg-blue-500/10 text-blue-400" title="View">
                        <Eye className="w-3.5 h-3.5" />
                      </Link>
                      {!r.deposit_paid && (
                        <button onClick={() => handleMarkPaid(r._id)} data-testid={`mark-paid-${r._id}`} className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-400" title="Mark Paid">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`flex items-center justify-between px-4 py-3 border-t ${ctx.borderColor}`}>
            <p className={`text-xs ${ctx.textSecondary}`}>Showing {(page - 1) * perPage + 1}-{Math.min(page * perPage, filtered.length)} of {filtered.length}</p>
            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i} onClick={() => setPage(i + 1)}
                  className={`px-3 py-1 rounded text-xs ${page === i + 1 ? 'bg-blue-600 text-white' : `${ctx.hoverBg} ${ctx.textSecondary}`}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
